import { useState, useCallback } from 'react';
import axios from 'axios';
import { formatRequestDay } from './ServiceRequestCard';

// The "N new service requests" notice, shared by the Dashboard and the
// Customers page, in the intake signups notice's place and manner. It lists
// the open requests on-call customers made in the portal since the owner last
// pressed Dismiss (GET /api/service-requests/new): a name and its dates per
// customer. Dismiss records the highest id shown in companies.settings
// (POST /api/service-requests/dismiss), so a request made after that shows
// again. Requests the owner entered on a customer card never appear here.

export function useServiceRequestsNotice() {
  const [notice, setNotice] = useState({ count: 0, throughId: 0, items: [] });
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState('');

  // A failure hides the notice rather than reaching the page around it.
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/service-requests/new`);
      const data = res?.data || {};
      setNotice({
        count: Number(data.count) || 0,
        throughId: Number(data.throughId) || 0,
        items: Array.isArray(data.items) ? data.items : [],
      });
    } catch (err) {
      setNotice({ count: 0, throughId: 0, items: [] });
    }
  }, []);

  // Hidden only once the server has recorded it.
  const dismiss = useCallback(async (throughId) => {
    setDismissing(true);
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/service-requests/dismiss`, { throughId });
      setNotice({ count: 0, throughId: 0, items: [] });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not dismiss the notice. Please try again.');
    } finally {
      setDismissing(false);
    }
  }, []);

  return { notice, dismissing, error, load, dismiss };
}

// Absent when there is nothing new. The pages decide who sees it (owner and
// admin, like the endpoint).
export default function ServiceRequestsNotice({ notice, dismissing, error, onDismiss }) {
  if (!notice || !notice.count || !notice.items || notice.items.length === 0) return null;
  return (
    <section className="sr-notice-card" aria-labelledby="sr-notice-title">
      <h2 id="sr-notice-title" className="sr-notice-title">
        {notice.count} new service {notice.count === 1 ? 'request' : 'requests'}
      </h2>
      {error && <p className="sr-notice-error" role="alert">{error}</p>}
      <ul className="sr-notice-list">
        {notice.items.map((item) => (
          <li key={item.customerId} className="sr-notice-row">
            <span className="sr-notice-name">{item.name}</span>
            <span className="sr-notice-dates">{item.dates.map(formatRequestDay).join(', ')}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="btn btn-outline btn-sm"
        onClick={() => onDismiss(notice.throughId)}
        disabled={dismissing}
      >
        {dismissing ? 'Saving...' : 'Dismiss'}
      </button>
      <style>{styles}</style>
    </section>
  );
}

const styles = `
  .sr-notice-card {
    background: #eff6ff;
    border: 1px solid #93c5fd;
    color: #1e3a8a;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 16px;
  }
  .sr-notice-title { font-size: 1.05rem; margin: 0 0 8px; color: #1e40af; }
  .sr-notice-error { margin: 0 0 8px; color: var(--color-error); font-weight: 600; }
  .sr-notice-list { list-style: none; margin: 0 0 10px; padding: 0; display: flex; flex-direction: column; gap: 6px; }
  .sr-notice-row {
    display: flex; flex-wrap: wrap; gap: 4px 12px; background: #fff;
    border: 1px solid #bfdbfe; border-radius: 8px; padding: 8px 12px;
  }
  .sr-notice-name { font-weight: 700; color: #1f2937; }
  .sr-notice-dates { color: #374151; }
  @media (max-width: 600px) {
    .sr-notice-card .btn { width: 100%; min-height: 44px; }
  }
`;
